import { Typography, Button, Grid, useMediaQuery } from "@mui/material";
import {
  FunctionField,
  Labeled,
  ReferenceField,
  SelectField,
  SimpleShowLayout,
  TextField,
  useNotify,
  useRefresh,
} from "react-admin";
import { checkPermission } from "../../../server/security/utils";
import { getPermissions } from "../../../auth/security/securityUtils";
import permissions from "../../../app/permissions";
import UserInfoForm from "./UserInfoForm";
import { GenderOptionsId } from "../../../utils/choices";
import { candidateRequests } from "api/candidateApi";
import { useState } from "react";
import { settings } from "app";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { administrationRequests } from "api/administrationApi";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";
import { SectionTitle } from "features/SectionTitle";

const UserData = ({ record, isForeigner }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();
  const [isUserInfoEditing, setIsUserInfoEditing] = useState(false);
  const [userInfoFormData, setUserInfoFormData] = useState(record?.userInfoDto || {});
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  const handleUserInfoEditClick = () => {
    setIsUserInfoEditing(true);
  };

  const handleSaveClick = async () => {
    try {
      await dataProvider.customRequest(
        candidateRequests.updateCandidacyInfo(record.id),
        { userInfoDto: userInfoFormData },
        "PUT",
      );
      notify("Данните са запазени успешно!", { type: "success" });
      setIsUserInfoEditing(false);
      refresh();
    } catch (error) {
      notifyError(error);
    }
  };

  const handleChange = (field, value) => {
    setUserInfoFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const statusLabel = (
    <div style={{ display: "flex", gap: 2 }}>
      <InfoOutlinedIcon fontSize="small" />
      <span>Статус на кандидатурата</span>
    </div>
  );

  return (
    <SimpleShowLayout>
      <Grid
        container
        justifyContent="flex-end"
      >
        <Grid
          item
          xs={12}
          md={2}
          sx={{
            ...(isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox),
            maxWidth: "none", // shadowBox's cap assumes its 2rem side margins, which are dropped here
            margin: 0,
            borderLeft: "none",
            boxShadow: "none",
          }}
        >
          <ReferenceField
            label=""
            source="stateId"
            reference={candidateRequests.getCandidacieStatesSelect()}
          >
            <FunctionField
              render={record => (
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: record.id === 1 ? "#2e7d32" : "#9D1B53",
                  }}
                >
                  {record.displayTextBg}
                </Typography>
              )}
            />
          </ReferenceField>
        </Grid>
      </Grid>
      <SectionTitle
        title="Данни за кандидата"
        textAlign="center"
        fontSize={18}
      />
      <Grid
        container
        spacing={3}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        {checkPermission(getPermissions(), permissions.canUpdateUserInfoCandidacies) && isUserInfoEditing ? (
          <UserInfoForm
            isBg={!isForeigner}
            handleChange={handleChange}
            handleSaveClick={handleSaveClick}
            setIsUserInfoEditing={setIsUserInfoEditing}
          />
        ) : (
          <>
            <Grid
              item
              xs={12}
              md={2}
            >
              <Labeled label="Име">
                <TextField
                  source="info.firstName"
                  emptyText="-"
                />
              </Labeled>
            </Grid>

            <Grid
              item
              xs={12}
              md={2}
            >
              <Labeled label="Презиме">
                <TextField
                  source="info.secondName"
                  emptyText="-"
                />
              </Labeled>
            </Grid>

            <Grid
              item
              xs={12}
              md={2}
            >
              <Labeled label="Фамилия">
                <TextField
                  source="info.lastName"
                  emptyText="-"
                />
              </Labeled>
            </Grid>
            {isForeigner ? (
              <>
                <Grid
                  item
                  xs={12}
                  md={2}
                >
                  <Labeled label="Име (латиница)">
                    <TextField
                      source="info.firstNameEn"
                      emptyText="-"
                    />
                  </Labeled>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={2}
                >
                  <Labeled label="Презиме (латиница)">
                    <TextField
                      source="info.secondNameEn"
                      emptyText="-"
                    />
                  </Labeled>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={2}
                >
                  <Labeled label="Фамилия (латиница)">
                    <TextField
                      source="info.lastNameEn"
                      emptyText="-"
                    />
                  </Labeled>
                </Grid>
              </>
            ) : null}
            {!isForeigner ? (
              <Grid
                item
                xs={12}
                md={2}
              >
                <Labeled label="Телефон за контакт">
                  <TextField
                    source="info.contactPhone"
                    emptyText="-"
                  />
                </Labeled>
              </Grid>
            ) : null}

            {!isForeigner ? (
              <Grid
                item
                xs={12}
                md={2}
              >
                <Labeled label="ЕГН">
                  <TextField
                    source="info.personId"
                    emptyText="-"
                  />
                </Labeled>
              </Grid>
            ) : null}

            <Grid
              item
              xs={12}
              md={2}
            >
              <Labeled label="Пол">
                <SelectField
                  source="info.gender"
                  choices={GenderOptionsId}
                  emptyText="-"
                />
              </Labeled>
            </Grid>
            {isForeigner ? (
              <Grid
                item
                xs={12}
                md={2}
              >
                <Labeled label="Държава">
                  <ReferenceField
                    source="info.residenceCountryId"
                    reference={administrationRequests.getCountriesSelect()}
                    emptyText="-"
                  >
                    <TextField source="nameBg" />
                  </ReferenceField>
                </Labeled>
              </Grid>
            ) : null}

            <Grid
              item
              xs={12}
              md={8}
            >
              <Labeled label="Имейл">
                <TextField source="email" />
              </Labeled>
            </Grid>
          </>
        )}
        {checkPermission(getPermissions(), permissions.canUpdateUserInfoCandidacies) && !isUserInfoEditing ? (
          <Grid
            item
            xs={12}
            md={4}
          >
            <Button
              disabled={record?.hasApprovedRanking}
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleUserInfoEditClick}
              sx={{ fontSize: 12, fontWeight: 400 }}
            >
              Редактирай данни за кандидата
            </Button>
          </Grid>
        ) : null}
      </Grid>
    </SimpleShowLayout>
  );
};

export default UserData;
